import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import OG_IMAGES from "@/data/ogImages.json";
import STATIC_HEADS from "@/data/staticHeads.json";
import BLOG_HEADS from "@/data/blogHeads.json";

/**
 * RouteSeo — the SINGLE SOURCE OF TRUTH for per-route head tags.
 * Sets <title>, meta description, canonical, Open Graph and Twitter tags for
 * every mapped route (and every blog slug). Page components no longer write
 * title/description/og/canonical themselves — they keep only JSON-LD + keywords.
 * Dependency-free (direct DOM) so it also works under puppeteer prerendering.
 * Identity: author is always Temple Franklin.
 */
const SITE = "https://mindbodyspirithygienetools.com";
const BRAND = "Mind Body Spirit HYGIENE™ Tools";
const DEFAULT_OG = `${SITE}/share-card.png`;

// /book and /build-your-own carry their own JSON-LD (prices that match the page).
// On those routes the site-wide static ItemList + FAQPage blocks from index.html
// are switched off so no other product prices or questions appear in the markup.
const OWN_LD_ROUTES = ["/book", "/build-your-own"];
const applyRouteJsonLd = (path) => {
  document.querySelectorAll('script[type^="application/ld+json"]').forEach((s) => {
    if (s.id || s.dataset.blogIndexSeo || s.dataset.siteLd) return;
    const t = s.textContent || "";
    if (t.includes('"ItemList"') || t.includes('"FAQPage"')) s.dataset.siteLd = "1";
  });
  const off = OWN_LD_ROUTES.includes(path);
  document.querySelectorAll('script[data-site-ld="1"]').forEach((s) => {
    s.setAttribute("type", off ? "application/ld+json+disabled" : "application/ld+json");
  });
};

// /home duplicates / — keep it working but out of the index.
const applyRobots = (path) => {
  const el = document.head.querySelector('meta[name="robots"]');
  if (!el) return;
  if (!el.dataset.defaultContent) el.dataset.defaultContent = el.getAttribute("content");
  el.setAttribute("content", STATIC_HEADS[path]?.robots || el.dataset.defaultContent);
};

// title <= 60 chars, description <= 155 chars, all unique.
const ROUTES = {
  // "/", "/book", "/build-your-own" live in staticHeads.json — the same values are
  // written into their raw HTML at build time (scripts/static-heads.js).
  ...Object.fromEntries(
    Object.entries(STATIC_HEADS)
      .filter(([, h]) => h.title)
      .map(([route, h]) => [route, { ...h, ogImage: h.ogImage && `${SITE}${h.ogImage}`, noClip: true }])
  ),
  "/free-chapter": {
    title: 'Read a Free Chapter — The "I Get To" Book | Temple Franklin',
    description:
      "Read a free chapter of the I GET TO book by Temple Franklin — real tools for real women in the messy middle. Then grab the paperback or instant PDF.",
    ogType: "book",
    ogImage: `${SITE}/book-cover-2026.png`,
    canonical: `${SITE}/book`,
  },
  "/free-sample": {
    title: 'Read a Free Chapter — The "I Get To" Book | Temple Franklin',
    description:
      "Read a free chapter of the I GET TO book by Temple Franklin — real tools for real women in the messy middle. Then grab the paperback or instant PDF.",
    ogType: "book",
    ogImage: `${SITE}/book-cover-2026.png`,
    canonical: `${SITE}/book`,
  },
  "/free": {
    title: "Free Self-Help Printables & Workbooks — Temple Franklin",
    description:
      "Every free resource from Temple Franklin in one place — the I GET TO workbook, Yes. No. Wait. cards, Validation Journal, card pulls, and the book sample.",
  },
  "/workbook": {
    title: 'Free "I Get To" Mindset Workbook — 27 Printable Pages',
    description:
      "Download the free 27-page I GET TO Workbook — real-tool reframes for the messy middle. Instant PDF, no cost, from Temple Franklin.",
  },
  "/tap-in-calendar": {
    title: "Free Printable 12-Month Daily Practice Calendar",
    description:
      "Free printable 12-month I GET TO daily-practice calendar from Temple Franklin — 365 gentle reframes, one for every day. Instant download.",
    ogImage: `${SITE}/og/tap-in-calendar.png`,
  },
  "/code-builder": {
    title: "Free Personal Code of Conduct Builder (Interactive)",
    description:
      "Build your own personal code of conduct with a free interactive tool by Temple Franklin that turns 'I have to' into 'I get to.'",
  },
  "/about": {
    title: "About Temple Franklin | Mind Body Spirit HYGIENE™",
    description:
      "Meet Temple Franklin — evidential medium, HeartMath-certified, 6th great-granddaughter of Benjamin Franklin, and founder of Mind Body Spirit HYGIENE™.",
  },
  "/contact": {
    title: "Contact Temple Franklin | Mind Body Spirit HYGIENE™",
    description:
      "Reach Temple Franklin — questions, speaking, and support for the I GET TO method and Mind Body Spirit HYGIENE™ Tools.",
  },
  "/the-method": {
    title: "The Mind Body Spirit HYGIENE™ Method — All 5 Card Decks",
    description:
      "Temple Franklin’s Mind Body Spirit HYGIENE™ Method — five printable PDF card decks: I GET TO, REWIRE, TRUE NORTH, THE BODY KNOWS, KIND & CLEAR.",
    ogImage: `${SITE}/i-get-to-deck/cover-card.png`,
  },
  "/boundaries": {
    title: "The Boundary Builder — 20 Scripts to Say No Kindly",
    description:
      "The Boundary Builder by Temple Franklin — 20 word-for-word scripts for people who struggle to say no, with family, friends, and coworkers. Kindly.",
  },
  "/hygiene-trilogy": {
    title: "The Hygiene Trilogy — Mind, Body & Spirit Mini E-Books",
    description:
      "Three soulful mini e-books — Mind Hygiene, Body Hygiene, Spirit Hygiene. Caveman-simple tools from Temple Franklin. Companion to the I GET TO book.",
    ogType: "product",
    ogImage: `${SITE}/downloads/mini-library-gumroad-cover-1280x720.png`,
  },
};



// A post's own /og/<slug>.png wins; otherwise the branded blog card. Never the course card.
const OWN_OG = new Set(OG_IMAGES);
export const blogOgImage = (slug) => `${SITE}/og/${OWN_OG.has(slug) ? slug : "blog"}.png`;

const clip = (s, n) => (s && s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);

const setTag = (selector, attr, value) => {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

export default function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.replace(/\/+$/, "") || "/";
    applyRouteJsonLd(path);
    applyRobots(path);
    let meta = ROUTES[path];
    let ogType = meta?.ogType || "website";
    let ogImage = meta?.ogImage || DEFAULT_OG;
    let canonical = meta?.canonical || `${SITE}${path === "/" ? "/" : path}`;

    // Blog posts: scripts/blog-heads.js builds BLOG_HEADS; the same values go into raw HTML.
    const blogHead = !meta && path.startsWith("/blog/") && BLOG_HEADS[path.replace("/blog/", "")];
    if (blogHead) {
      meta = { ...blogHead, noClip: true };
      ogType = blogHead.ogType;
      ogImage = `${SITE}${blogHead.ogImage}`;
    }
    if (!meta) return; // leave page-managed heads (unmapped routes) untouched

    const description = meta.noClip ? meta.description : clip(meta.description, 155);
    document.title = meta.title;
    setTag('meta[name="description"]', "content", description);
    setTag('link[rel="canonical"]', "href", canonical);
    setTag('meta[property="og:title"]', "content", meta.ogTitle || meta.title);
    setTag('meta[property="og:description"]', "content", description);
    setTag('meta[property="og:url"]', "content", canonical);
    setTag('meta[property="og:type"]', "content", ogType);
    setTag('meta[property="og:site_name"]', "content", BRAND);
    setTag('meta[property="og:image"]', "content", ogImage);
    setTag('meta[name="twitter:card"]', "content", "summary_large_image");
    setTag('meta[name="twitter:title"]', "content", meta.ogTitle || meta.title);
    setTag('meta[name="twitter:description"]', "content", description);
    setTag('meta[name="twitter:image"]', "content", ogImage);
  }, [pathname]);

  return null;
}
